import { Component, computed, inject, input, InputSignal, output, OutputEmitterRef } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EstimationDialogData } from '@app/components/dialogs/estimation-dialog/estimation-dialog.component';
import { IntlCurrencyPipe } from '@app/shared/pipes/intl-currency.pipe';
import { IntlDurationPipe } from '@app/shared/pipes/intl-duration.pipe';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { QuantityUnits } from '@app/shared/types/enums/quantity-units.enum';
import { TaskEstimation } from '@app/shared/types/models/task/task-estimation';
import { TranslatePipe } from '@ngx-translate/core';

const QUANTITY_UNIT_TRANSLATION_KEYS: Record<QuantityUnits, string> = {
  [QuantityUnits.PIECES]: 'quantity-units.pieces',
  [QuantityUnits.METERS]: 'quantity-units.meters',
  [QuantityUnits.METERS_SQUARE]: 'quantity-units.meters-square',
  [QuantityUnits.METERS_CUBIC]: 'quantity-units.meters-cubic',
  [QuantityUnits.SETS]: 'quantity-units.sets'
};

@Component({
  selector: 'proffeo-task-estimation-select',
  templateUrl: './task-estimation-select.component.html',
  imports: [IntlCurrencyPipe, IntlDurationPipe, MatIconModule, MatTooltipModule, TranslatePipe],
  host: {
    class: 'block min-w-0 max-w-full'
  }
})
export class TaskEstimationSelectComponent {
  private dialogService: DialogService = inject(DialogService);

  protected readonly timeEstimationPresent = computed(() => {
    const time = this.estimation()?.time;
    return time !== null && time !== undefined;
  });

  protected readonly costEstimationPresent = computed(() => {
    const amount = this.estimation()?.financial?.amount;
    return amount !== null && amount !== undefined && amount > 0;
  });

  protected readonly quantityEstimationPresent = computed(() => {
    const amount = this.estimation()?.quantity?.amount;
    return amount !== null && amount !== undefined && amount > 0;
  });

  protected readonly quantityEstimationAmount = computed(() => this.estimation()?.quantity?.amount);

  protected readonly quantityUnitTranslationKey = computed(() => {
    const unit = this.estimation()?.quantity?.unit;
    return unit ? QUANTITY_UNIT_TRANSLATION_KEYS[unit] : '';
  });

  protected readonly estimatedQuantityLabelKey = 'project-tasks.list.column-headers.estimated-quantity';

  public readonly estimation: InputSignal<TaskEstimation | undefined> = input<TaskEstimation | undefined>();
  public readonly showTime = input(true);
  public readonly showCost = input(true);
  public readonly showQuantity = input(true);
  public readonly estimationSelected: OutputEmitterRef<TaskEstimation> = output<TaskEstimation>();

  protected openEstimationDialog(): void {
    const data: EstimationDialogData = {
      estimation: this.estimation(),
      autoFocus: false
    };

    this.dialogService
      .openEstimationDialog(data)
      .afterClosed()
      .subscribe((result: TaskEstimation | undefined) => {
        if (result) {
          this.estimationSelected.emit(result);
        }
      });
  }
}
